// Copyright (c) 2021-2026 Broadcom. All Rights Reserved.
// Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
// and/or its subsidiaries.
import { NgModule } from "@angular/core";
import { RouterModule, Routes, TitleStrategy } from "@angular/router";
import { Error403Component } from "@shared/components/error403/error403.component";
import { RequireLoggedInGuard } from "@vmw/ngx-csp-oauth2";
import { RoleGuard } from "@app/guards/role.guard";
import { VLR_CONSOLE } from "@app/modules/vlr-console/vlr-console-routing-util";
import { OPERATOR_CONSOLE } from "@op-console/op-console-routing-util";
import { VMCFS_CONSOLE } from "@vmcfs-console/vmcfs-console-routing-util";
import { QUORUM_CONSOLE } from "@app/modules/quorum-console/quorum-console-routing-util";
import { Error404Component } from "@shared/components/error404/error404.component";
import { environment } from "../environments/environment";
import { ServiceTypeEnum } from "@api/generated/vcdr";
import { ServiceState } from "@app/services/app-config/model/app-config";
import { LocalizedTitleStrategy } from "@app/services/title/localized-title-strategy";

const APP_ROUTES: Routes = [
    {
        path: "oauth2",
        children: [
            {
                path: "error",
                component: Error403Component
            },
            {
                path: "callback",
                redirectTo: ""
            }
        ]
    },

    // Operator console
    {
        path: OPERATOR_CONSOLE.ROOT,
        canActivate: [RequireLoggedInGuard, RoleGuard],
        loadChildren: () => import("@app/modules/op-console/op-console.module").then(m => m.OpConsoleModule)
    },

    // VCDR console
    {
        path: VLR_CONSOLE.ROOT,
        canActivate: [RequireLoggedInGuard, RoleGuard],
        loadChildren: () => import("@app/modules/vlr-console/vlr-console.module").then(m => m.VlrConsoleModule)
    },

    {
        path: VLR_CONSOLE.LEGACY_ROOT,
        redirectTo: VLR_CONSOLE.ROOT
    },


    // Storage console
    {
        path: VMCFS_CONSOLE.ROOT,
        canActivate: [RequireLoggedInGuard, RoleGuard],
        loadChildren: () => import("@app/modules/vmcfs-console/vmcfs-console.module").then(m => m.VmcfsConsoleModule)
    },

    // Quorum console
    {
        path: QUORUM_CONSOLE.ROOT,
        canActivate: [RequireLoggedInGuard, RoleGuard],
        loadChildren: () => import("@app/modules/quorum-console/quorum-console.module").then(m => m.QuorumConsoleModule)
    },

    // Currently we hit this scenario in Dev environment but not In Production
    {
        path: "",
        canActivate: [RoleGuard],
        children: [],
        pathMatch: "full"
    },

    {
        path: "**",
        component: Error404Component
    }
];

const CONSOLE_ROOT_URLS: Map<ServiceTypeEnum, string> = new Map(
    [
        [ServiceTypeEnum.VCDR, VLR_CONSOLE.ROOT],
        [ServiceTypeEnum.VMCFS, VMCFS_CONSOLE.ROOT],
        [ServiceTypeEnum.VMUA, QUORUM_CONSOLE.ROOT]
    ]);

// If a self-service console is not enabled, replace the route configuration so it displays the error page.
environment.services.forEach((serviceConfig, serviceType) => {
    if (serviceConfig.serviceState < ServiceState.ENABLED_SELF_SERVICE) {
        const consoleRootUrl = CONSOLE_ROOT_URLS.get(serviceType);
        const consoleRoute: number = APP_ROUTES.findIndex(val => val.path === consoleRootUrl);
        APP_ROUTES[consoleRoute] = {
            path: consoleRootUrl,
            component: Error404Component
        };
    }
});

// @formatter:off
@NgModule({
    imports: [RouterModule.forRoot(APP_ROUTES, { onSameUrlNavigation: "reload" })],
    exports: [RouterModule],
    providers: [
        { provide: TitleStrategy, useClass: LocalizedTitleStrategy }
    ]
})
// @formatter:on

export class AppRoutingModule {
}
